import React, { useEffect, useState } from "react";
import { get, ref } from "firebase/database";
import { database } from "../firebaseConfig";

// Mirrors functions/helpers/contactMedia.js: screenshots, website photos and
// logos are reused for new images for REUSE_DAYS after they were made; every
// entry is deleted at its expiresAt (pushed out each time it is used again).
const REUSE_DAYS = 60;
const DAY_MS = 24 * 60 * 60 * 1000;

const KINDS = {
  generated: { label: "Sales Bots", icon: "fa-wand-magic-sparkles", reusable: false },
  websiteScreenshot: { label: "Website screenshot", icon: "fa-display", reusable: true },
  websitePhoto: { label: "Website photo", icon: "fa-image", reusable: true },
  brandLogo: { label: "Logo", icon: "fa-copyright", reusable: true },
};

export const contactKeyOf = (contactId) => (contactId ? String(contactId).replace(/[.#$/[\]]/g, "_") : "");

const formatDate = (ms) =>
  new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const daysLeft = (ms, now) => Math.max(0, Math.ceil((ms - now) / DAY_MS));

const safeImage = (url) => (typeof url === "string" && /^https:\/\//i.test(url) ? url : "");

/** Live media rows for a contact, newest first. */
export const liveMedia = (node, now = Date.now()) =>
  Object.entries(node && typeof node === "object" ? node : {})
    .map(([id, row]) => ({ id, ...(row || {}) }))
    .filter((row) => KINDS[row.kind] && safeImage(row.imageUrl) && (row.expiresAt || 0) > now)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

const ContactMediaInsight = ({ userId, contactId }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setRows([]);
    const key = contactKeyOf(contactId);
    if (!userId || !key) return undefined;
    let cancelled = false;
    try {
      get(ref(database, `World/${userId}/contactMedia/${key}`))
        .then((snap) => {
          if (!cancelled) setRows(liveMedia(snap.val()));
        })
        .catch(() => {});
    } catch (_) {
      /* database unavailable */
    }
    return () => {
      cancelled = true;
    };
  }, [userId, contactId]);

  if (!rows.length) return null;
  const now = Date.now();

  return (
    <section className="ir-section">
      <div className="ir-section-label">
        <i className="fa fa-images" />
        Images
        <span className="ir-section-count">{rows.length}</span>
      </div>
      <div className="ir-media-grid">
        {rows.map((row) => {
          const kind = KINDS[row.kind];
          const reuseUntil = (row.createdAt || 0) + REUSE_DAYS * DAY_MS;
          const reusing = kind.reusable && reuseUntil > now;
          return (
            <figure key={row.id} className="ir-media-card">
              <a
                className="ir-media-thumb"
                href={safeImage(row.imageUrl)}
                target="_blank"
                rel="noopener noreferrer"
                title="Open full size"
              >
                <img src={safeImage(row.imageUrl)} alt={row.label || kind.label} loading="lazy" />
              </a>
              <figcaption className="ir-media-meta">
                <span className="ir-media-kind">
                  <i className={`fa ${kind.icon}`} />
                  {kind.label}
                </span>
                {row.label && row.label !== kind.label && <span className="ir-media-label">{row.label}</span>}
                {kind.reusable && (
                  <span className={`ir-media-date ${reusing ? "ir-media-date--live" : ""}`}>
                    <i className="fa fa-rotate" />
                    {reusing
                      ? `Reused until ${formatDate(reuseUntil)} (${daysLeft(reuseUntil, now)}d)`
                      : "Reuse ended, the next image captures a fresh one"}
                  </span>
                )}
                <span className="ir-media-date">
                  <i className="fa fa-hourglass-half" />
                  Deleted {formatDate(row.expiresAt)} ({daysLeft(row.expiresAt, now)}d)
                </span>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </section>
  );
};

export default ContactMediaInsight;
